import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import GoogleShipmentMap from '../components/GoogleShipmentMap';
import ManualShipmentModal from '../components/ManualShipmentModal';
import useIsMobile from '../hooks/useIsMobile';
import { openUrl } from '../utils/nativeFiles';
import { assignShipmentDriver, completeShipment, discardShipment, downloadOrderFile, getEwayBillDownloadUrl, getGeneratedInvoices, getInvoiceDownloadUrl, getOrderFiles, getShipment as getShipmentApi, getShipmentOrderMoveTargets, moveShipmentOrder, recordShipmentDelivery, removeOrderFromShipment, startShipment, trackShipmentLocation, updateShipmentVehicleType } from '../config/api';
import './Shipments.css';

const STATUS_LABELS = { pending: 'Pending', started: 'Started', completed: 'Completed' };
const VEHICLE_LABELS = { twoWheeler: 'Two-wheeler', fourWheeler: 'Four-wheeler' };
const LOCATION_CACHE_MAX_AGE_MS = 2 * 60 * 1000;
const DELIVERY_LOCATION_CACHE_MAX_AGE_MS = 15 * 1000;
const LOCATION_MAX_ACCURACY_METERS = 500;

const getCurrentUser = () => {
  try { return JSON.parse(localStorage.getItem('userData_30jan') || '{}'); }
  catch (_) { return {}; }
};

const formatDateTime = (value, fallback = 'Not started') => value ? new Date(value).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : fallback;

const formatDate = (value) => value ? new Date(`${value}T00:00:00+05:30`).toLocaleDateString('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
}) : '—';


const reusableLocation = (location, maxAgeMs = LOCATION_CACHE_MAX_AGE_MS) => {
  if (!location || !Number.isFinite(Number(location.latitude)) || !Number.isFinite(Number(location.longitude))) return null;
  const accuracy = location.accuracyMeters === null || location.accuracyMeters === undefined
    ? null
    : Number(location.accuracyMeters);
  const capturedAt = Number(location.capturedAt || 0);
  if (accuracy !== null && (!Number.isFinite(accuracy) || accuracy > LOCATION_MAX_ACCURACY_METERS)) return null;
  if (!capturedAt || Date.now() - capturedAt > maxAgeMs) return null;
  return location;
};

const geolocationMessage = (error) => ({
  1: 'Location permission is required for a delivery trip.',
  2: 'Your current location is unavailable. Check GPS and network access.',
  3: 'Location request timed out. Please try again in an open area.',
}[error?.code] || 'Could not capture the current location.');

// Robust GPS capture. Tries a high-accuracy fix first (best on a phone), and if
// that times out or is unavailable, automatically falls back to a fast
// network/Wi-Fi fix (works on desktops and weak-signal spots) before failing.
// A permission denial (code 1) fails immediately — no fallback would help.
const getDeviceLocation = (cachedLocation = null, maxAgeMs = LOCATION_CACHE_MAX_AGE_MS) => new Promise((resolve, reject) => {
  const recentLocation = reusableLocation(cachedLocation, maxAgeMs);
  if (recentLocation) {
    resolve(recentLocation);
    return;
  }
  if (!navigator.geolocation) return reject(new Error('Location is not supported on this device.'));

  const toLocation = (position) => ({
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracyMeters: position.coords.accuracy,
    capturedAt: Number(position.timestamp) || Date.now(),
  });

  // Second attempt: low-accuracy, allows a slightly stale cached fix.
  const fallbackAttempt = () => navigator.geolocation.getCurrentPosition(
    (position) => resolve(toLocation(position)),
    (error) => reject(new Error(geolocationMessage(error))),
    { enableHighAccuracy: false, timeout: 12000, maximumAge: 120000 },
  );

  // First attempt: high-accuracy, longer timeout.
  navigator.geolocation.getCurrentPosition(
    (position) => resolve(toLocation(position)),
    (error) => {
      if (error?.code === 1) { reject(new Error(geolocationMessage(error))); return; }
      fallbackAttempt();
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: Math.min(maxAgeMs, 60000) },
  );
});

const formatDistance = (meters) => Number(meters || 0) >= 1000 ? `${(Number(meters) / 1000).toFixed(1)} km` : `${Math.round(Number(meters || 0))} m`;

const formatDuration = (seconds) => {
  const totalMinutes = Math.max(0, Math.round(Number(seconds || 0) / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours ? `${hours} hr ${minutes} min` : `${minutes} min`;
};

// --- Mobile helpers -------------------------------------------------------
// Compact route value for a chip, e.g. "37.0 km · 1h 40m".
const formatRouteCompact = (shipment) => {
  if (!shipment?.distanceMeters || !shipment?.durationSeconds) return 'Route pending';
  const distance = shipment.distanceMeters >= 1000
    ? `${(shipment.distanceMeters / 1000).toFixed(1)} km`
    : `${shipment.distanceMeters} m`;
  const totalMinutes = Math.max(1, Math.round(shipment.durationSeconds / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${distance} · ${hours ? `${hours}h ${minutes}m` : `${minutes}m`}`;
};

// One summary line above the orders, e.g. "Hub → 7 stops → Hub · 390.8 km · 7h 59m".
const routeSummaryLine = (shipment) => {
  const stopCount = (shipment.stops || []).length;
  const stops = `${stopCount} ${stopCount === 1 ? 'stop' : 'stops'}`;
  const route = formatRouteCompact(shipment);
  return route === 'Route pending'
    ? `Hub → ${stops} → Hub`
    : `Hub → ${stops} → Hub · ${route}`;
};

// Google Maps directions link for an order's delivery stop. When a confirmed
// GPS pin from a previous delivery to this address exists (stop.location, only
// present when locationSource === 'confirmed'), navigate to those exact
// coordinates. Otherwise fall back to the typed address (Google geocodes it).
const navigationUrl = (order, stop) => {
  const lat = Number(stop?.location?.latitude);
  const lng = Number(stop?.location?.longitude);
  const hasConfirmedPin = stop?.locationSource === 'confirmed'
    && Number.isFinite(lat) && Number.isFinite(lng);
  const destination = hasConfirmedPin
    ? `${lat},${lng}`
    : (order?.address || [stop?.area, order?.pincode].filter(Boolean).join(', ') || order?.pincode || '');
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
};

// True when this stop has a reusable confirmed GPS pin from a past delivery.
const hasConfirmedPin = (stop) => stop?.locationSource === 'confirmed'
  && Number.isFinite(Number(stop?.location?.latitude))
  && Number.isFinite(Number(stop?.location?.longitude));

// One muted line under the header with the shipment source and start state.
const formatRunStartedLine = (shipment) => {
  const run = shipment.planningMode === 'manual' ? `Manual shipment ${shipment.runId}` : `Daily run ${shipment.runId}`;
  if (!shipment.startedAt) return `${run} · Not started`;
  const when = new Date(shipment.startedAt).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit',
  });
  return shipment.driver ? `${run} · Started ${when} by ${shipment.driver}` : `${run} · Started ${when}`;
};

const BackIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon = () => (
  <svg className="shipment-chip-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg className="shipment-chip-lock" width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const OrdersIcon = () => (
  <svg className="shipment-chip-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5v-9Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M3.5 7.5 12 12m0 0 8.5-4.5M12 12v9" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const RouteIcon = () => (
  <svg className="shipment-chip-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="6" cy="18" r="2.2" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="18" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 17h6a3 3 0 0 0 0-6H9a3 3 0 0 1 0-6h3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const NavigateIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M21 3 3 10.5l7 2.5 2.5 7L21 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6.5 4h3l1.2 3.2-1.8 1.4a12 12 0 0 0 5 5l1.4-1.8L18 13v3a2 2 0 0 1-2.2 2A14 14 0 0 1 4.5 6.7 2 2 0 0 1 6.5 4Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
  </svg>
);

const DocIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M7 3h7l4 4v14H7z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    <path d="M14 3v4h4M9.5 13h5M9.5 16h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const ExpandIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 4H4v5M15 4h5v5M15 20h5v-5M9 20H4v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 11v5M12 7.5v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const DriverChipIcon = () => (
  <svg className="shipment-chip-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M5.5 19a6.5 6.5 0 0 1 13 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const VehicleChipIcon = () => (
  <svg className="shipment-chip-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 14l1.6-4.5h11L18 14v4H3v-4Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    <path d="M18 11h2.2l1.3 2.2V18H18" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    <circle cx="7" cy="18.5" r="1.6" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="17" cy="18.5" r="1.6" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);

// Focus-trap + Esc handling shared by the mobile bottom sheets.
const useBottomSheet = (open, onClose) => {
  const sheetRef = useRef(null);
  // Keep the latest onClose in a ref so callers can pass inline arrows without
  // re-running the effect (which would reset focus and the scroll lock).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const node = sheetRef.current;
    const focusable = () => node
      ? Array.from(node.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter((el) => !el.disabled && el.offsetParent !== null)
      : [];
    const first = focusable()[0];
    if (first) first.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') { event.stopPropagation(); onCloseRef.current(); return; }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) { event.preventDefault(); lastItem.focus(); }
      else if (!event.shiftKey && document.activeElement === lastItem) { event.preventDefault(); firstItem.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, true);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, [open]);
  return sheetRef;
};

const ShipmentDetail = () => {
  const { shipmentId } = useParams();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const canViewAssignment = currentUser.role === 'superAdmin' || currentUser.access?.includes('deliveryAdmin');
  const canOpenOrders = canViewAssignment;
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [vehicleInput, setVehicleInput] = useState('');
  const [startOdometer, setStartOdometer] = useState(null);
  const [endOdometer, setEndOdometer] = useState(null);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [deliveryOdometer, setDeliveryOdometer] = useState(null);
  const [deliveryOtps, setDeliveryOtps] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [editOrders, setEditOrders] = useState(false);
  const [showAddOrders, setShowAddOrders] = useState(false);
  const [orderToRemove, setOrderToRemove] = useState(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [discardConfirmation, setDiscardConfirmation] = useState('');
  const [orderToMove, setOrderToMove] = useState(null);
  const [moveTargets, setMoveTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState('');
  const [moveTargetsLoading, setMoveTargetsLoading] = useState(false);
  const [moveError, setMoveError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [pendingPin, setPendingPin] = useState(null);
  const [selectedDeliveryOrders, setSelectedDeliveryOrders] = useState([]);
  const [deliveryNote, setDeliveryNote] = useState('');
  const [locationStatus, setLocationStatus] = useState('');
  const [deliverySaving, setDeliverySaving] = useState(false);
  const [deliveryPhase, setDeliveryPhase] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [vehicleTypeSaving, setVehicleTypeSaving] = useState(false);
  const [activeSheet, setActiveSheet] = useState(null); // 'driver' | 'vehicle' | null
  const [focusedStop, setFocusedStop] = useState(null); // card↔map sync (desktop)
  const orderCardRefs = useRef({}); // sequence -> card DOM node, for map→card scroll
  const isMobile = useIsMobile();
  const trackingRequestRef = useRef(false);
  const lastTrackingSentRef = useRef(0);
  const deliveryRequestRef = useRef(false);

  const canEditOrders = Boolean(shipment?.canEdit && shipment.orderCount > 0);
  const canAddOrders = Boolean(shipment?.canEdit && shipment?.status === 'pending');

  const loadShipment = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getShipmentApi(shipmentId);
      setShipment(response.data);
      setSelectedDriverId(response.data?.assignedTo?.id ? String(response.data.assignedTo.id) : '');
    } catch (requestError) {
      setError(requestError.message || 'Could not load shipment');
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => { loadShipment(); }, [loadShipment]);

  useEffect(() => {
    if (!shipment?.canDrive || shipment.status !== 'started' || !navigator.geolocation) return undefined;
    const watchId = navigator.geolocation.watchPosition(position => {
      const location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracyMeters: position.coords.accuracy,
        capturedAt: Number(position.timestamp) || Date.now(),
      };
      setCurrentLocation(location);
      setLocationStatus(position.coords.accuracy > 100 ? `GPS accuracy ±${Math.round(position.coords.accuracy)} m` : 'Live GPS active');
      const now = Date.now();
      if (trackingRequestRef.current || now - lastTrackingSentRef.current < 15000) return;
      trackingRequestRef.current = true;
      lastTrackingSentRef.current = now;
      trackShipmentLocation(shipment.id, location).catch(requestError => {
        setLocationStatus(requestError.message || 'Live location could not be saved');
      }).finally(() => { trackingRequestRef.current = false; });
    }, locationError => {
      setLocationStatus(locationError.code === 1 ? 'Allow location access to track this trip' : 'Waiting for a reliable GPS signal');
    }, { enableHighAccuracy: true, timeout: 20000, maximumAge: 10000 });
    return () => navigator.geolocation.clearWatch(watchId);
  }, [shipment?.canDrive, shipment?.id, shipment?.status]);

  useEffect(() => {
    if (!shipment?.nextStop || selectedDeliveryOrders.length) return;
    setSelectedDeliveryOrders((shipment.nextStop.orderIds || []).filter(orderId => !shipment.nextStop.deliveredOrderIds?.includes(orderId)));
  }, [shipment?.nextStop, selectedDeliveryOrders.length]);

  const handleStart = async () => {
    const vehicle = vehicleInput.trim().toUpperCase();
    if (!vehicle) return;
    setActionLoading(true);
    setError('');
    try {
      setLocationStatus('Capturing departure location…');
      const location = await getDeviceLocation(currentLocation);
      setCurrentLocation(location);
      await startShipment(shipment.id, vehicle, location, startOdometer);
      setShowStartDialog(false);
      setVehicleInput('');
      setStartOdometer(null);
      setLocationStatus('Live GPS active');
      await loadShipment();
    } catch (requestError) {
      setError(requestError.message || 'Could not start shipment');
    } finally {
      setActionLoading(false);
    }
  };

  // Assign driver (used by the driver sheet on mobile and desktop): toast
  // feedback, rollback on failure, sheet closes on success.
  const handleAssignmentMobile = async (driverId) => {
    const nextDriverId = driverId || '';
    if (nextDriverId === String(shipment.assignedTo?.id || '')) { setActiveSheet(null); return; }
    const previousShipment = shipment;
    const previousSelected = selectedDriverId;
    setAssignmentSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await assignShipmentDriver(shipment.id, nextDriverId || null);
      setShipment(response.data);
      setSelectedDriverId(response.data?.assignedTo?.id ? String(response.data.assignedTo.id) : '');
      setActiveSheet(null);
      toast.success(response.data?.assignedTo
        ? `${response.data.assignedTo.name || response.data.assignedTo.email} assigned`
        : 'Shipment is now unassigned');
    } catch (requestError) {
      setShipment(previousShipment);
      setSelectedDriverId(previousSelected);
      toast.error(requestError.message || 'Could not update the assignment');
    } finally {
      setAssignmentSaving(false);
    }
  };

  // Change vehicle type (vehicle sheet): same feedback and rollback as above.
  const handleVehicleTypeMobile = async (vehicleType) => {
    if (!vehicleType || vehicleType === shipment.vehicleType) { setActiveSheet(null); return; }
    const previousShipment = shipment;
    setVehicleTypeSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await updateShipmentVehicleType(shipment.id, vehicleType);
      setShipment(response.data);
      setSelectedDriverId(response.data?.assignedTo?.id ? String(response.data.assignedTo.id) : '');
      setActiveSheet(null);
      toast.success(`Vehicle changed to ${VEHICLE_LABELS[response.data?.vehicleType] || 'Four-wheeler'}`);
    } catch (requestError) {
      setShipment(previousShipment);
      toast.error(requestError.message || 'Could not update the vehicle type');
    } finally {
      setVehicleTypeSaving(false);
    }
  };

  // The end odometer photo is captured in the end-ride dialog, so clicking
  // "End ride" opens that dialog; the dialog's confirm calls handleComplete.
  const handleComplete = async () => {
    setActionLoading(true);
    setError('');
    try {
      setLocationStatus('Capturing ending location…');
      const location = await getDeviceLocation(currentLocation);
      setCurrentLocation(location);
      await completeShipment(shipment.id, location, endOdometer);
      setShowEndDialog(false);
      setEndOdometer(null);
      setLocationStatus('Ride completed');
      await loadShipment();
    } catch (requestError) {
      setError(requestError.message || 'Could not complete shipment');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleDeliveryOrder = (orderId) => {
    setSelectedDeliveryOrders(current => current.includes(orderId) ? current.filter(id => id !== orderId) : [...current, orderId]);
  };

  const handleRecordDelivery = async () => {
    if (!selectedDeliveryOrders.length || deliveryRequestRef.current) return;
    if (!deliveryOdometer) { setError('Snap the odometer photo at this stop before confirming delivery.'); return; }
    const missingOtp = selectedDeliveryOrders.find((orderId) => !String(deliveryOtps[orderId] || '').trim());
    if (missingOtp) { setError(`Enter the customer OTP for order ${missingOtp}.`); return; }
    const otpPayload = selectedDeliveryOrders.reduce((acc, orderId) => {
      acc[orderId] = String(deliveryOtps[orderId] || '').trim();
      return acc;
    }, {});

    deliveryRequestRef.current = true;
    setDeliverySaving(true);
    setDeliveryPhase('location');
    setError('');
    setSuccessMessage('');
    setPendingPin(null);
    try {
      setLocationStatus('Capturing delivery location…');
      const location = await getDeviceLocation(currentLocation, DELIVERY_LOCATION_CACHE_MAX_AGE_MS);
      setCurrentLocation(location);
      setPendingPin(location);
      setLocationStatus(`Delivery location captured · accuracy ±${Math.round(location.accuracyMeters || 0)} m`);
      setDeliveryPhase('saving');
      const response = await recordShipmentDelivery(shipment.id, selectedDeliveryOrders, location, deliveryNote, otpPayload, deliveryOdometer);
      setShipment(response.data);
      setSuccessMessage(`${selectedDeliveryOrders.length} order${selectedDeliveryOrders.length > 1 ? 's' : ''} delivered at this stop.`);
      setSelectedDeliveryOrders([]);
      setPendingPin(null);
      setDeliveryNote('');
      setDeliveryOdometer(null);
      setDeliveryOtps({});
    } catch (requestError) {
      setError(requestError.message || 'Could not record this delivery stop');
    } finally {
      deliveryRequestRef.current = false;
      setDeliverySaving(false);
      setDeliveryPhase('');
    }
  };

  // Per-order delivery from a single order card (OTP required; photo + note
  // optional). Reuses the existing GPS capture + recordShipmentDelivery action,
  // one order at a time. Returns true on success so the card can collapse.
  const handleDeliverOrder = async (orderId, { otp, note, odometerFile }) => {
    if (deliveryRequestRef.current) return false;
    const trimmedOtp = String(otp || '').trim();
    if (!trimmedOtp) { toast.error('Enter the customer OTP to confirm delivery.'); return false; }
    deliveryRequestRef.current = true;
    setError('');
    setSuccessMessage('');
    try {
      const location = await getDeviceLocation(currentLocation, DELIVERY_LOCATION_CACHE_MAX_AGE_MS);
      setCurrentLocation(location);
      const response = await recordShipmentDelivery(
        shipment.id,
        [orderId],
        location,
        note || '',
        { [orderId]: trimmedOtp },
        odometerFile || undefined,
      );
      setShipment(response.data);
      toast.success(`Order ${orderId} delivered.`);
      return true;
    } catch (requestError) {
      toast.error(requestError.message || 'Could not record this delivery.');
      return false;
    } finally {
      deliveryRequestRef.current = false;
    }
  };

  const openMoveDialog = async (order) => {
    setOrderToMove(order);
    setMoveTargets([]);
    setSelectedTarget('');
    setMoveError('');
    setMoveTargetsLoading(true);
    try {
      const response = await getShipmentOrderMoveTargets(shipment.id, order.id);
      const targets = response.data?.targets || [];
      setMoveTargets(targets);
      const firstAvailable = targets.find(target => target.available);
      if (firstAvailable) setSelectedTarget(firstAvailable.id);
    } catch (requestError) {
      setMoveError(requestError.message || 'Could not load compatible shipments');
    } finally {
      setMoveTargetsLoading(false);
    }
  };

  const closeMoveDialog = (force = false) => {
    if (actionLoading && !force) return;
    setOrderToMove(null);
    setMoveTargets([]);
    setSelectedTarget('');
    setMoveError('');
  };

  const handleMoveOrder = async () => {
    if (!orderToMove || !selectedTarget) return;
    setActionLoading(true);
    setMoveError('');
    setSuccessMessage('');
    try {
      const movedOrderId = orderToMove.id;
      const response = await moveShipmentOrder(shipment.id, movedOrderId, selectedTarget);
      const result = response.data;
      closeMoveDialog(true);
      if (result.sourceDeleted) {
        navigate(`/shipments/${result.target.id}`, { replace: true });
        return;
      }
      setShipment(result.source);
      setSuccessMessage(`Order ${movedOrderId} moved to ${result.target.id}. Both delivery routes were refreshed.`);
    } catch (requestError) {
      setMoveError(requestError.message || 'Could not move order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveOrder = async () => {
    if (!orderToRemove || actionLoading) return;
    setActionLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const removedOrderId = orderToRemove.id;
      const response = await removeOrderFromShipment(shipment.id, removedOrderId);
      const updated = response.data;
      if (!updated?.id) throw new Error('Order was removed but the refreshed shipment was not returned');
      setShipment(updated);
      setOrderToRemove(null);
      if (updated.routeStatus === 'failed') toast.warning('Order removed. The road route is unavailable, so address markers are being used.');
      else toast.success(`${removedOrderId} removed and the route was refreshed.`);
      setSuccessMessage(`Order ${removedOrderId} was released back to the planning pool.`);
    } catch (requestError) {
      const message = requestError.message || 'Could not remove the order';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDiscardShipment = async () => {
    if (discardConfirmation.trim() !== shipment.id || actionLoading) return;
    setActionLoading(true);
    setError('');
    try {
      const response = await discardShipment(shipment.id);
      toast.success(`${shipment.id} discarded. ${response.data?.releasedOrderIds?.length || 0} order(s) returned to planning.`);
      navigate('/shipments', { replace: true });
    } catch (requestError) {
      const message = requestError.message || 'Could not discard the shipment';
      setError(message);
      toast.error(message);
      setShowDiscardDialog(false);
      setDiscardConfirmation('');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !shipment) return <div className="container"><div className="card shipments-empty">Loading shipment…</div></div>;
  if (!shipment) return <div className="container"><div className="card shipments-empty">{error || 'Shipment not found'} · <Link to="/shipments">Back to shipments</Link></div></div>;

  const vehicleLabel = VEHICLE_LABELS[shipment.vehicleType] || VEHICLE_LABELS.fourWheeler;
  const utilization = shipment.capacityPoints ? Math.round((shipment.loadPoints / shipment.capacityPoints) * 100) : 0;
  const remainingCapacity = Math.max(0, shipment.capacityPoints - shipment.loadPoints);
  const pointsAboveTarget = Math.max(0, shipment.loadPoints - shipment.capacityPoints);
  const routeStops = shipment.stops || [];
  const stopByOrderId = new Map(routeStops.flatMap(stop => (stop.orderIds || []).map(orderId => [String(orderId), stop])));

  const driverEditable = shipment.status === 'pending' && shipment.canAssign;
  const vehicleEditable = shipment.status === 'pending' && shipment.canEditVehicleType;
  const driverName = shipment.assignedTo?.name || shipment.assignedTo?.email || 'Unassigned';
  // Map pin click → focus + scroll the matching order card into view (desktop).
  const handleStopSelect = (sequence) => {
    setFocusedStop(sequence);
    const node = orderCardRefs.current[String(sequence)];
    if (node?.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  // Orders ordered by their route stop sequence (fallback: keep original order).
  const orderedOrders = [...(shipment.orders || [])].sort((a, b) => {
    const sa = stopByOrderId.get(String(a.id))?.sequence ?? Number.MAX_SAFE_INTEGER;
    const sb = stopByOrderId.get(String(b.id))?.sequence ?? Number.MAX_SAFE_INTEGER;
    return sa - sb;
  });

  if (isMobile) {
    return (
      <div className="container shipment-detail-page shipment-detail-mobile">
        {error && <div className="shipment-api-error">{error}</div>}
        {successMessage && <div className="shipment-edit-success">{successMessage}</div>}

        <MobileHeader
          shipment={shipment}
          vehicleLabel={vehicleLabel}
          driverName={driverName}
          utilization={utilization}
          pointsAboveTarget={pointsAboveTarget}
          canViewAssignment={canViewAssignment}
          driverEditable={driverEditable}
          vehicleEditable={vehicleEditable}
          assignmentSaving={assignmentSaving}
          vehicleTypeSaving={vehicleTypeSaving}
          actionLoading={actionLoading}
          onBack={() => navigate('/shipments')}
          onEditDriver={() => (driverEditable ? setActiveSheet('driver') : toast.info('Driver is locked once the ride starts.'))}
          onEditVehicle={() => (vehicleEditable ? setActiveSheet('vehicle') : toast.info('Vehicle is locked once the ride starts.'))}
          onStart={() => setShowStartDialog(true)}
          onEnd={() => setShowEndDialog(true)}
        />

        {shipment.status === 'pending' && shipment.startBlockedBy && <div className="shipment-start-blocked-notice">
          <div><span>One shipment at a time</span><strong>Complete {shipment.startBlockedBy.id} before starting this shipment.</strong></div>
          <Link to={`/shipments/${shipment.startBlockedBy.id}`}>Continue active shipment <span aria-hidden="true">→</span></Link>
        </div>}

        <MobileOrderList
          orders={orderedOrders}
          orderCount={shipment.orderCount}
          routeSummary={routeSummaryLine(shipment)}
          nextStopSequence={shipment.status === 'started' ? shipment.nextStop?.sequence ?? null : null}
          rideStarted={shipment.status === 'started'}
          stopByOrderId={stopByOrderId}
          canOpenOrders={canOpenOrders}
          canEditOrders={canEditOrders}
          canAddOrders={canAddOrders}
          canDiscard={canAddOrders}
          editOrders={editOrders}
          actionLoading={actionLoading}
          onToggleEdit={() => { setEditOrders(value => !value); closeMoveDialog(); setSuccessMessage(''); }}
          onAddOrders={() => setShowAddOrders(true)}
          onDiscard={() => { setDiscardConfirmation(''); setShowDiscardDialog(true); }}
          onMove={openMoveDialog}
          onRemove={setOrderToRemove}
          onDeliverOrder={handleDeliverOrder}
          cardRefs={orderCardRefs}
          focusedStop={focusedStop}
          onFocusStop={setFocusedStop}
        />

        {canViewAssignment && <MobileLoadCard
          shipment={shipment}
          vehicleLabel={vehicleLabel}
          utilization={utilization}
          remainingCapacity={remainingCapacity}
          pointsAboveTarget={pointsAboveTarget}
        />}

        <MobileMapCard shipment={shipment} currentLocation={currentLocation} pendingPin={pendingPin} focusedStop={focusedStop} onStopSelect={handleStopSelect} />

        {activeSheet === 'driver' && <DriverSheet
          shipment={shipment}
          currentDriverId={String(shipment.assignedTo?.id || '')}
          saving={assignmentSaving}
          onClose={() => !assignmentSaving && setActiveSheet(null)}
          onSave={handleAssignmentMobile}
        />}

        {activeSheet === 'vehicle' && <VehicleSheet
          shipment={shipment}
          saving={vehicleTypeSaving}
          onClose={() => !vehicleTypeSaving && setActiveSheet(null)}
          onSave={handleVehicleTypeMobile}
        />}

        {showStartDialog && <div className="shipment-start-overlay" role="presentation" onMouseDown={() => !actionLoading && setShowStartDialog(false)}><div className="shipment-start-dialog" role="dialog" aria-modal="true" aria-labelledby="start-shipment-title-m" onMouseDown={event => event.stopPropagation()}><div className="shipment-start-dialog-header"><div><h2 id="start-shipment-title-m">Start Shipment</h2><p>{shipment.id} · {vehicleLabel}</p></div><button type="button" aria-label="Close" disabled={actionLoading} onClick={() => setShowStartDialog(false)}>×</button></div><label className="form-label" htmlFor="shipment-vehicle-number-m">Vehicle number</label><input id="shipment-vehicle-number-m" className="form-control" value={vehicleInput} onChange={event => setVehicleInput(event.target.value.toUpperCase())} placeholder="e.g. UP 32 AB 1234" autoFocus /><OdometerCapture id="start-odometer-m" label="Add odometer photo (optional)" file={startOdometer} onChange={setStartOdometer} disabled={actionLoading} /><p className="shipment-start-time-note">The departure time and GPS location will be captured automatically. Keep location permission enabled during the ride.</p>{locationStatus && <p className="shipment-start-location-status">{locationStatus}</p>}<div className="shipment-start-dialog-actions"><button type="button" className="btn btn-secondary" disabled={actionLoading} onClick={() => setShowStartDialog(false)}>Cancel</button><button type="button" className="btn btn-success" disabled={!vehicleInput.trim() || actionLoading} onClick={handleStart}>{actionLoading ? 'Capturing GPS…' : 'Start Shipment'}</button></div></div></div>}
      {showEndDialog && <div className="shipment-start-overlay" role="presentation" onMouseDown={() => !actionLoading && setShowEndDialog(false)}><div className="shipment-start-dialog" role="dialog" aria-modal="true" aria-labelledby="end-shipment-title-m" onMouseDown={event => event.stopPropagation()}><div className="shipment-start-dialog-header"><div><h2 id="end-shipment-title-m">End ride</h2><p>{shipment.id} · {vehicleLabel}</p></div><button type="button" aria-label="Close" disabled={actionLoading} onClick={() => setShowEndDialog(false)}>×</button></div><OdometerCapture id="end-odometer-m" label="Add odometer photo (optional)" file={endOdometer} onChange={setEndOdometer} disabled={actionLoading} /><p className="shipment-start-time-note">The ending time and GPS location are captured automatically. The ride can end away from the hub.</p>{locationStatus && <p className="shipment-start-location-status">{locationStatus}</p>}<div className="shipment-start-dialog-actions"><button type="button" className="btn btn-secondary" disabled={actionLoading} onClick={() => setShowEndDialog(false)}>Cancel</button><button type="button" className="btn btn-success" disabled={actionLoading} onClick={handleComplete}>{actionLoading ? 'Ending…' : 'End ride'}</button></div></div></div>}

        {orderToMove && <MobileMoveSheet
          orderToMove={orderToMove}
          moveTargets={moveTargets}
          selectedTarget={selectedTarget}
          moveTargetsLoading={moveTargetsLoading}
          moveError={moveError}
          actionLoading={actionLoading}
          onSelect={setSelectedTarget}
          onClose={() => closeMoveDialog()}
          onConfirm={handleMoveOrder}
        />}
        {showAddOrders && <ManualShipmentModal shipment={shipment} onClose={() => setShowAddOrders(false)} onOrdersAdded={(updated) => { setShipment(updated); setShowAddOrders(false); setSuccessMessage('Shipment orders and route updated.'); }} />}
        {orderToRemove && <ShipmentDangerDialog mode="remove" shipment={shipment} order={orderToRemove} busy={actionLoading} onClose={() => !actionLoading && setOrderToRemove(null)} onConfirm={handleRemoveOrder} />}
        {showDiscardDialog && <ShipmentDangerDialog mode="discard" shipment={shipment} confirmation={discardConfirmation} onConfirmationChange={setDiscardConfirmation} busy={actionLoading} onClose={() => { if (!actionLoading) { setShowDiscardDialog(false); setDiscardConfirmation(''); } }} onConfirm={handleDiscardShipment} />}
      </div>
    );
  }

  return (
    <div className="container shipment-detail-page">
      <div className="shipment-detail-breadcrumb"><Link to="/shipments">Shipments</Link><span>/</span><strong>{shipment.id}</strong></div>
      {error && <div className="shipment-api-error">{error}</div>}
      {successMessage && <div className="shipment-edit-success">{successMessage}</div>}

      <MobileHeader
        shipment={shipment}
        vehicleLabel={vehicleLabel}
        driverName={driverName}
        utilization={utilization}
        pointsAboveTarget={pointsAboveTarget}
        canViewAssignment={canViewAssignment}
        driverEditable={driverEditable}
        vehicleEditable={vehicleEditable}
        assignmentSaving={assignmentSaving}
        vehicleTypeSaving={vehicleTypeSaving}
        actionLoading={actionLoading}
        onBack={() => navigate('/shipments')}
        onEditDriver={() => (driverEditable ? setActiveSheet('driver') : toast.info('Driver is locked once the ride starts.'))}
        onEditVehicle={() => (vehicleEditable ? setActiveSheet('vehicle') : toast.info('Vehicle is locked once the ride starts.'))}
        onStart={() => setShowStartDialog(true)}
        onEnd={() => setShowEndDialog(true)}
      />

      {shipment.status === 'pending' && shipment.startBlockedBy && <div className="shipment-start-blocked-notice">
        <div><span>One shipment at a time</span><strong>Complete {shipment.startBlockedBy.id} before starting this shipment.</strong></div>
        <Link to={`/shipments/${shipment.startBlockedBy.id}`}>Continue active shipment <span aria-hidden="true">→</span></Link>
      </div>}

      {(shipment.status === 'started' || shipment.status === 'completed') && <ShipmentTripPanel
        shipment={shipment}
        currentLocation={currentLocation}
        pendingPin={pendingPin}
        selectedOrders={selectedDeliveryOrders}
        deliveryNote={deliveryNote}
        locationStatus={locationStatus}
        actionLoading={actionLoading || deliverySaving}
        deliveryPhase={deliveryPhase}
        odometerFile={deliveryOdometer}
        onOdometerChange={setDeliveryOdometer}
        otps={deliveryOtps}
        onOtpChange={(orderId, value) => setDeliveryOtps((current) => ({ ...current, [orderId]: value }))}
        onToggleOrder={toggleDeliveryOrder}
        onNoteChange={setDeliveryNote}
        onRecordDelivery={handleRecordDelivery}
        onComplete={() => setShowEndDialog(true)}
      />}

      <div className="shipment-d-layout">
        <div className="shipment-d-orders-col">
          <MobileOrderList
            orders={orderedOrders}
            orderCount={shipment.orderCount}
            routeSummary={routeSummaryLine(shipment)}
            nextStopSequence={shipment.status === 'started' ? shipment.nextStop?.sequence ?? null : null}
            stopByOrderId={stopByOrderId}
            canOpenOrders={canOpenOrders}
            canEditOrders={canEditOrders}
            canAddOrders={canAddOrders}
            canDiscard={canAddOrders}
            editOrders={editOrders}
            actionLoading={actionLoading}
            onToggleEdit={() => { setEditOrders(value => !value); closeMoveDialog(); setSuccessMessage(''); }}
            onAddOrders={() => setShowAddOrders(true)}
            onDiscard={() => { setDiscardConfirmation(''); setShowDiscardDialog(true); }}
            onMove={openMoveDialog}
            onRemove={setOrderToRemove}
            cardRefs={orderCardRefs}
            focusedStop={focusedStop}
            onFocusStop={setFocusedStop}
          />
          {canViewAssignment && <MobileLoadCard
            shipment={shipment}
            vehicleLabel={vehicleLabel}
            utilization={utilization}
            remainingCapacity={remainingCapacity}
            pointsAboveTarget={pointsAboveTarget}
          />}
        </div>
        <DesktopMapPanel
          shipment={shipment}
          currentLocation={currentLocation}
          pendingPin={pendingPin}
          focusedStop={focusedStop}
          onStopSelect={handleStopSelect}
        />
      </div>

      {activeSheet === 'driver' && <DriverSheet
        shipment={shipment}
        currentDriverId={String(shipment.assignedTo?.id || '')}
        saving={assignmentSaving}
        onClose={() => !assignmentSaving && setActiveSheet(null)}
        onSave={handleAssignmentMobile}
      />}
      {activeSheet === 'vehicle' && <VehicleSheet
        shipment={shipment}
        saving={vehicleTypeSaving}
        onClose={() => !vehicleTypeSaving && setActiveSheet(null)}
        onSave={handleVehicleTypeMobile}
      />}

      {showStartDialog && <div className="shipment-start-overlay" role="presentation" onMouseDown={() => !actionLoading && setShowStartDialog(false)}><div className="shipment-start-dialog" role="dialog" aria-modal="true" aria-labelledby="start-shipment-title" onMouseDown={event => event.stopPropagation()}><div className="shipment-start-dialog-header"><div><h2 id="start-shipment-title">Start Shipment</h2><p>{shipment.id} · {vehicleLabel}</p></div><button type="button" aria-label="Close" disabled={actionLoading} onClick={() => setShowStartDialog(false)}>×</button></div><label className="form-label" htmlFor="shipment-vehicle-number">Vehicle number</label><input id="shipment-vehicle-number" className="form-control" value={vehicleInput} onChange={event => setVehicleInput(event.target.value.toUpperCase())} placeholder="e.g. UP 32 AB 1234" autoFocus /><OdometerCapture id="start-odometer" label="Add odometer photo (optional)" file={startOdometer} onChange={setStartOdometer} disabled={actionLoading} /><p className="shipment-start-time-note">The departure time and GPS location will be captured automatically. Keep location permission enabled during the ride.</p>{locationStatus && <p className="shipment-start-location-status">{locationStatus}</p>}<div className="shipment-start-dialog-actions"><button type="button" className="btn btn-secondary" disabled={actionLoading} onClick={() => setShowStartDialog(false)}>Cancel</button><button type="button" className="btn btn-success" disabled={!vehicleInput.trim() || actionLoading} onClick={handleStart}>{actionLoading ? 'Capturing GPS…' : 'Start Shipment'}</button></div></div></div>}
      {showEndDialog && <div className="shipment-start-overlay" role="presentation" onMouseDown={() => !actionLoading && setShowEndDialog(false)}><div className="shipment-start-dialog" role="dialog" aria-modal="true" aria-labelledby="end-shipment-title" onMouseDown={event => event.stopPropagation()}><div className="shipment-start-dialog-header"><div><h2 id="end-shipment-title">End ride</h2><p>{shipment.id} · {vehicleLabel}</p></div><button type="button" aria-label="Close" disabled={actionLoading} onClick={() => setShowEndDialog(false)}>×</button></div><OdometerCapture id="end-odometer" label="Add odometer photo (optional)" file={endOdometer} onChange={setEndOdometer} disabled={actionLoading} /><p className="shipment-start-time-note">The ending time and GPS location are captured automatically. The ride can end away from the hub.</p>{locationStatus && <p className="shipment-start-location-status">{locationStatus}</p>}<div className="shipment-start-dialog-actions"><button type="button" className="btn btn-secondary" disabled={actionLoading} onClick={() => setShowEndDialog(false)}>Cancel</button><button type="button" className="btn btn-success" disabled={actionLoading} onClick={handleComplete}>{actionLoading ? 'Ending…' : 'End ride'}</button></div></div></div>}
      {orderToMove && <div className="shipment-start-overlay" role="presentation" onMouseDown={() => closeMoveDialog()}>
        <div className="shipment-start-dialog shipment-move-order-dialog" role="dialog" aria-modal="true" aria-labelledby="move-shipment-order-title" onMouseDown={event => event.stopPropagation()}>
          <div className="shipment-start-dialog-header"><div><h2 id="move-shipment-order-title">Move complete order</h2><p>{orderToMove.id} · {orderToMove.customer}</p></div><button type="button" aria-label="Close" disabled={actionLoading} onClick={() => closeMoveDialog()}>×</button></div>
          <div className="shipment-move-rule"><strong>Same hub only</strong><span>All products move together. Planning points are advisory for a manual move, and both routes will be regenerated.</span></div>
          {moveTargetsLoading ? <div className="shipment-move-loading">Finding compatible shipments…</div> : moveError ? <div className="shipment-api-error">{moveError}</div> : moveTargets.length ? <div className="shipment-move-targets">{moveTargets.map(target => {
            const projectedLoad = Number(target.projectedLoadPoints ?? target.loadPoints) || 0;
            const percent = target.capacityPoints ? Math.round((projectedLoad / target.capacityPoints) * 100) : 0;
            return <label key={target.id} className={`shipment-move-target ${selectedTarget === target.id ? 'is-selected' : ''} ${!target.available ? 'is-disabled' : ''} ${target.exceedsPlanningTarget ? 'is-over-target' : ''}`}>
              <input type="radio" name="targetShipment" value={target.id} checked={selectedTarget === target.id} disabled={!target.available || actionLoading} onChange={() => setSelectedTarget(target.id)} />
              <div className="shipment-move-target-main"><strong>{target.id}</strong><span>{target.area} · {(target.pincodes || []).join(', ')}</span><small>{VEHICLE_LABELS[target.vehicleType] || 'Four-wheeler'} · {target.orderCount} orders</small></div>
              <div className="shipment-move-target-load"><strong>{percent}%</strong><span>After move: {projectedLoad}/{target.capacityPoints} pts</span>{target.exceedsPlanningTarget && target.available && <small>Above planning target · move allowed</small>}{!target.available && <small>{target.unavailableReason}</small>}</div>
            </label>;
          })}</div> : <div className="shipment-move-empty"><strong>No target shipment available</strong><span>This hub has no other compatible pending shipment for this order.</span></div>}
          <div className="shipment-start-dialog-actions"><button type="button" className="btn btn-secondary" disabled={actionLoading} onClick={() => closeMoveDialog()}>Cancel</button><button type="button" className="shipment-confirm-move" disabled={!selectedTarget || actionLoading || moveTargetsLoading} onClick={handleMoveOrder}>{actionLoading ? 'Moving and rebuilding routes…' : 'Move order'}</button></div>
        </div>
      </div>}
      {showAddOrders && <ManualShipmentModal shipment={shipment} onClose={() => setShowAddOrders(false)} onOrdersAdded={(updated) => { setShipment(updated); setShowAddOrders(false); setSuccessMessage('Shipment orders and route updated.'); }} />}
      {orderToRemove && <ShipmentDangerDialog mode="remove" shipment={shipment} order={orderToRemove} busy={actionLoading} onClose={() => !actionLoading && setOrderToRemove(null)} onConfirm={handleRemoveOrder} />}
      {showDiscardDialog && <ShipmentDangerDialog mode="discard" shipment={shipment} confirmation={discardConfirmation} onConfirmationChange={setDiscardConfirmation} busy={actionLoading} onClose={() => { if (!actionLoading) { setShowDiscardDialog(false); setDiscardConfirmation(''); } }} onConfirm={handleDiscardShipment} />}
    </div>
  );
};


const ShipmentDangerDialog = ({ mode, shipment, order, confirmation = '', onConfirmationChange, busy, onClose, onConfirm }) => {
  const discarding = mode === 'discard';
  const confirmed = !discarding || confirmation.trim() === shipment.id;
  return <div className="shipment-start-overlay" role="presentation" onMouseDown={onClose}>
    <div className="shipment-start-dialog shipment-danger-dialog" role="dialog" aria-modal="true" aria-labelledby="shipment-danger-title" onMouseDown={event => event.stopPropagation()}>
      <div className="shipment-start-dialog-header">
        <div><h2 id="shipment-danger-title">{discarding ? 'Discard shipment?' : 'Remove order from shipment?'}</h2><p>{discarding ? shipment.id : `${order?.id} · ${order?.customer || 'Customer'}`}</p></div>
        <button type="button" aria-label="Close" disabled={busy} onClick={onClose}>×</button>
      </div>
      <div className="shipment-danger-warning">
        <strong>{discarding ? `All ${shipment.orderCount} orders will be released.` : 'The complete order will be released.'}</strong>
        <span>No product will be split or deleted. Released orders can be selected manually and may be picked up by the next automatic planning run.</span>
      </div>
      {discarding && <label className="shipment-danger-confirm"><span>Type <b>{shipment.id}</b> to confirm</span><input type="text" value={confirmation} disabled={busy} autoFocus autoComplete="off" onChange={event => onConfirmationChange(event.target.value)} placeholder={shipment.id} /></label>}
      <div className="shipment-start-dialog-actions">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="button" className="shipment-danger-confirm-button" disabled={busy || !confirmed} onClick={onConfirm}>{busy ? (discarding ? 'Discarding…' : 'Removing…') : (discarding ? 'Discard shipment' : 'Remove order')}</button>
      </div>
    </div>
  </div>;
};



// Camera-capture control for the mandatory odometer photo. Opens the rear
// camera on mobile (capture="environment"), shows a thumbnail once captured,
// and lets the driver retake. Returns the File via onChange.
const OdometerCapture = ({ id, label, file, onChange, disabled }) => {
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  return (
    <div className="shipment-odometer">
      <span className="shipment-odometer-label">{label} <b aria-hidden="true">*</b></span>
      {file ? (
        <div className="shipment-odometer-preview">
          <img src={previewUrl} alt="Odometer" />
          <div className="shipment-odometer-preview-info">
            <span>Photo captured</span>
            <label className="shipment-odometer-retake">
              Retake
              <input type="file" accept="image/*" capture="environment" disabled={disabled}
                onChange={event => onChange(event.target.files?.[0] || null)} />
            </label>
          </div>
        </div>
      ) : (
        <label htmlFor={id} className={`shipment-odometer-button ${disabled ? 'is-disabled' : ''}`}>
          <span aria-hidden="true">📷</span> Snap odometer
          <input id={id} type="file" accept="image/*" capture="environment" disabled={disabled}
            onChange={event => onChange(event.target.files?.[0] || null)} />
        </label>
      )}
    </div>
  );
};

const ShipmentTripPanel = ({ shipment, currentLocation, pendingPin, selectedOrders, deliveryNote, locationStatus, actionLoading, deliveryPhase, odometerFile, onOdometerChange, otps = {}, onOtpChange, onToggleOrder, onNoteChange, onRecordDelivery, onComplete }) => {
  const missingOtp = selectedOrders.some((orderId) => !String(otps[orderId] || '').trim());
  const deliveryReady = selectedOrders.length && odometerFile && !missingOtp && !actionLoading;
  const progress = shipment.deliveryProgress || { deliveredOrders: 0, totalOrders: shipment.orderCount, completedStops: 0, remainingOrders: shipment.orderCount };
  const isCompleted = shipment.status === 'completed';
  const undeliveredOrders = (shipment.orders || []).filter(order => !order.delivered);
  const completedPercent = progress.totalOrders ? Math.round((progress.deliveredOrders / progress.totalOrders) * 100) : 0;

  return <section className={`card shipment-driver-panel ${isCompleted ? 'is-completed' : ''}`}>
    <div className="shipment-driver-header">
      <div><span className="shipment-driver-eyebrow">{isCompleted ? 'Ride summary' : 'Driver workspace'}</span><h2>{isCompleted ? 'Delivery trip completed' : 'Live delivery trip'}</h2><p>{isCompleted ? 'Actual trip activity and proof of delivery.' : 'Follow the planned checkpoints and pin each completed delivery.'}</p></div>
      {!isCompleted && <div className="shipment-live-location"><i />{locationStatus || (currentLocation ? 'Live GPS active' : 'Waiting for GPS')}</div>}
    </div>

    <div className="shipment-trip-stats">
      <div><span>Delivered</span><strong>{progress.deliveredOrders}/{progress.totalOrders}</strong><small>{completedPercent}% complete</small></div>
      <div><span>Drop points</span><strong>{progress.completedStops}</strong><small>Confirmed GPS pins</small></div>
      <div><span>{isCompleted ? 'Actual distance' : 'Planned distance'}</span><strong>{formatDistance(isCompleted ? shipment.trip?.actualDistanceMeters : shipment.distanceMeters)}</strong><small>{isCompleted ? 'Road distance through confirmed GPS stops' : 'Hub-to-hub route'}</small></div>
      <div><span>{isCompleted ? 'Total time' : 'Estimated time'}</span><strong>{formatDuration(isCompleted ? shipment.trip?.actualDurationSeconds : shipment.durationSeconds)}</strong><small>{isCompleted ? `${formatDateTime(shipment.trip?.startedAt)} → ${formatDateTime(shipment.trip?.endedAt)}` : 'Traffic-aware estimate'}</small></div>
      {isCompleted && <div><span>Ride ended</span><strong>{shipment.trip?.returnedToHub ? 'At hub' : 'Away from hub'}</strong><small>{shipment.trip?.endDistanceFromHubMeters === null ? 'End location saved' : `${formatDistance(shipment.trip?.endDistanceFromHubMeters)} from hub`}</small></div>}
    </div>

    {!isCompleted && shipment.canRecordDelivery && <div className="shipment-driver-workspace">
      <div className="shipment-next-stop-card">
        <div className="shipment-next-stop-number">{shipment.nextStop?.sequence || 'H'}</div>
        <div><span>{shipment.nextStop ? 'Next delivery stop' : 'Planned return'}</span><strong>{shipment.nextStop ? `${shipment.nextStop.area} · ${shipment.nextStop.pincode}` : `Return to ${shipment.hub}`}</strong><small>{shipment.nextStop ? `${shipment.nextStop.address} · ${shipment.nextStop.orderIds?.length || 0} order(s)` : `${shipment.hubAddress}. You may also end the ride at your current location.`}</small></div>
      </div>

      {undeliveredOrders.length > 0 && <div className="shipment-delivery-recorder">
        <div className="shipment-delivery-recorder-heading"><div><strong>Confirm delivery at this drop point</strong><span>Select every order delivered at the same location. Your current GPS location will be captured when you deliver.</span></div></div>
        <div className="shipment-delivery-order-grid">
          {undeliveredOrders.map(order => <label className={selectedOrders.includes(order.id) ? 'is-selected' : ''} key={order.id}><input type="checkbox" checked={selectedOrders.includes(order.id)} disabled={actionLoading} onChange={() => onToggleOrder(order.id)} /><div><strong>{order.id}</strong><span>{order.customer} · {order.pincode}</span></div></label>)}
        </div>
        {selectedOrders.length > 0 && <div className="shipment-delivery-otp-list">
          <div className="shipment-delivery-otp-heading"><strong>Customer OTP</strong><span>Enter the OTP the customer shares for each order.</span></div>
          {selectedOrders.map(orderId => <label className="shipment-delivery-otp-row" key={orderId}>
            <span className="shipment-delivery-otp-order">{orderId}</span>
            <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otps[orderId] || ''} disabled={actionLoading} placeholder="OTP" onChange={event => onOtpChange(orderId, event.target.value.replace(/\D/g, ''))} />
          </label>)}
        </div>}
        <OdometerCapture id="delivery-odometer" label="Odometer photo at this stop" file={odometerFile} onChange={onOdometerChange} disabled={actionLoading} />
        <div className="shipment-delivery-pin-row">
          <div className={`shipment-pin-status ${pendingPin ? 'is-ready' : 'is-auto'}`}><span>{pendingPin ? 'Location captured' : 'Automatic GPS'}</span><strong>{pendingPin ? `${Number(pendingPin.latitude).toFixed(6)}, ${Number(pendingPin.longitude).toFixed(6)}` : 'Captured when you tap Deliver'}</strong></div>
          <input type="text" maxLength="500" value={deliveryNote} onChange={event => onNoteChange(event.target.value)} placeholder="Optional delivery note" disabled={actionLoading} />
          <button type="button" className="shipment-confirm-delivery" disabled={!deliveryReady} onClick={onRecordDelivery}>{deliveryPhase === 'location' ? 'Getting location…' : deliveryPhase === 'saving' ? 'Saving delivery…' : `Deliver ${selectedOrders.length || ''} order${selectedOrders.length === 1 ? '' : 's'}`}</button>
        </div>
      </div>}

      <div className="shipment-end-ride-row"><div><strong>End ride from any location</strong><span>{progress.remainingOrders ? `${progress.remainingOrders} order(s) still need a delivery pin.` : 'All orders are delivered. Your current end location will be saved.'}</span></div><button type="button" className="shipment-end-ride-button" disabled={!shipment.canComplete || actionLoading} onClick={onComplete}>{actionLoading ? 'Ending…' : 'End ride'}</button></div>
    </div>}

    {(shipment.deliveryStops || []).length > 0 && <div className="shipment-delivery-timeline"><div className="shipment-delivery-timeline-title"><strong>Delivery timeline</strong><span>Orders delivered at each confirmed stop</span></div>{shipment.deliveryStops.map(stop => <div className="shipment-delivery-timeline-item" key={stop.id}><span className="shipment-timeline-sequence">{stop.sequence}</span><div><strong>{(stop.orderIds || []).join(', ')}</strong><span>{formatDateTime(stop.deliveredAt)} · {Number(stop.latitude).toFixed(6)}, {Number(stop.longitude).toFixed(6)}</span>{stop.note && <small>{stop.note}</small>}</div><b>{stop.orderIds?.length || 0} order{stop.orderIds?.length === 1 ? '' : 's'}</b></div>)}</div>}
  </section>;
};

// --- Mobile components ----------------------------------------------------

const MobileChip = ({ icon, label, editable, locked, onClick }) => {
  const interactive = editable || locked;
  return (
    <button
      type="button"
      className={`shipment-m-chip ${interactive ? 'is-interactive' : ''} ${locked ? 'is-locked' : ''}`}
      onClick={interactive ? onClick : undefined}
      disabled={!interactive}
    >
      {icon && <span className="shipment-m-chip-ico" aria-hidden="true">{icon}</span>}
      <span className="shipment-m-chip-label" title={label}>{label}</span>
      {editable && <ChevronIcon />}
      {!editable && locked && <LockIcon />}
    </button>
  );
};

const MobileHeader = ({
  shipment, vehicleLabel, driverName, utilization, pointsAboveTarget, canViewAssignment,
  driverEditable, vehicleEditable, assignmentSaving, vehicleTypeSaving, actionLoading,
  onBack, onEditDriver, onEditVehicle, onStart, onEnd,
}) => {
  const loadBarLabel = `${utilization}% · ${shipment.loadPoints}/${shipment.capacityPoints} pts`;
  const progress = shipment.deliveryProgress || { deliveredOrders: 0, totalOrders: shipment.orderCount };
  // Compact progress line: "Delivered 0/5 · 37.3 km · 1h 1m".
  const progressLine = `Delivered ${progress.deliveredOrders}/${progress.totalOrders} · ${formatRouteCompact(shipment)}`;
  return (
    <header className="shipment-m-header">
      <div className="shipment-m-header-top">
        <button type="button" className="shipment-m-back" aria-label="Back to shipments" onClick={onBack}><BackIcon /></button>
        <h1 className="shipment-m-id" title={shipment.id}>{shipment.id}</h1>
        <span className={`shipment-status shipment-status-${shipment.status}`}>{STATUS_LABELS[shipment.status]}</span>
        {shipment.status === 'pending' && shipment.canStart && (
          <button type="button" className="btn btn-success shipment-m-start" disabled={actionLoading} onClick={onStart}>
            {actionLoading ? '…' : 'Start ride'}
          </button>
        )}
        {shipment.status === 'started' && (
          <button type="button" className="btn shipment-m-start shipment-m-end" disabled={actionLoading || !shipment.canComplete} onClick={onEnd}>
            {actionLoading ? '…' : 'End ride'}
          </button>
        )}
      </div>

      <p className="shipment-m-runline" title={formatRunStartedLine(shipment)}>{formatRunStartedLine(shipment)}</p>
      {shipment.status !== 'pending' && <p className="shipment-m-progress">{progressLine}</p>}

      <div className="shipment-m-chips" role="list">
        <MobileChip icon={<OrdersIcon />} label={`${shipment.orderCount} ${shipment.orderCount === 1 ? 'order' : 'orders'}`} />
        {canViewAssignment && (
          <MobileChip
            icon={<DriverChipIcon />} label={driverName}
            editable={driverEditable && !assignmentSaving}
            locked={!driverEditable}
            onClick={onEditDriver}
          />
        )}
        {canViewAssignment && (
          <MobileChip
            icon={<VehicleChipIcon />} label={vehicleLabel}
            editable={vehicleEditable && !vehicleTypeSaving}
            locked={!vehicleEditable}
            onClick={onEditVehicle}
          />
        )}
        <MobileChip icon={<RouteIcon />} label={formatRouteCompact(shipment)} />
      </div>

      {canViewAssignment && (
        <div className="shipment-m-loadbar">
          <div className={`shipment-m-loadbar-track ${pointsAboveTarget ? 'is-over' : ''}`}>
            <span style={{ width: `${Math.min(utilization, 100)}%` }} />
          </div>
          <span className="shipment-m-loadbar-label">{loadBarLabel}</span>
        </div>
      )}
    </header>
  );
};

const DriverSheet = ({ shipment, currentDriverId, saving, onClose, onSave }) => {
  const sheetRef = useBottomSheet(true, onClose);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(currentDriverId);
  const drivers = shipment.assignableDrivers || [];
  const filtered = drivers.filter((driver) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [driver.name, driver.email].some((value) => String(value || '').toLowerCase().includes(q));
  });
  const changed = selected !== currentDriverId;

  return (
    <div className="shipment-m-sheet-overlay" role="presentation" onMouseDown={onClose}>
      <div className="shipment-m-sheet" role="dialog" aria-modal="true" aria-labelledby="driver-sheet-title" ref={sheetRef} onMouseDown={(event) => event.stopPropagation()}>
        <div className="shipment-m-sheet-handle" aria-hidden="true" />
        <div className="shipment-m-sheet-head">
          <h2 id="driver-sheet-title">Assign driver</h2>
          <button type="button" aria-label="Close" onClick={onClose} disabled={saving}>×</button>
        </div>
        <p className="shipment-m-sheet-note">You can change this until the ride starts.</p>
        <input
          className="shipment-m-sheet-search" type="search" value={query} placeholder="Search drivers…"
          onChange={(event) => setQuery(event.target.value)} aria-label="Search drivers"
        />
        <div className="shipment-m-sheet-list">
          <button type="button" className={`shipment-m-opt-row ${selected === '' ? 'is-selected' : ''}`} onClick={() => setSelected('')}>
            <span className="shipment-m-opt-avatar" aria-hidden="true">—</span>
            <span className="shipment-m-opt-copy"><strong>Unassigned</strong><span>Remove the current driver</span></span>
            {selected === '' && <span className="shipment-m-opt-check" aria-hidden="true">✓</span>}
          </button>
          {filtered.map((driver) => {
            const value = String(driver.id);
            return (
              <button type="button" key={driver.id} className={`shipment-m-opt-row ${selected === value ? 'is-selected' : ''}`} onClick={() => setSelected(value)}>
                <span className="shipment-m-opt-avatar" aria-hidden="true">{(driver.name || driver.email || '?').trim().charAt(0).toUpperCase()}</span>
                <span className="shipment-m-opt-copy">
                  <strong title={driver.name || driver.email}>{driver.name || driver.email}</strong>
                  <span title={driver.email}>{driver.email}</span>
                </span>
                {selected === value && <span className="shipment-m-opt-check" aria-hidden="true">✓</span>}
              </button>
            );
          })}
          {!drivers.length && <p className="shipment-m-sheet-empty">No active Delivery users are assigned to this hub.</p>}
          {drivers.length > 0 && !filtered.length && <p className="shipment-m-sheet-empty">No drivers match “{query}”.</p>}
        </div>
        <div className="shipment-m-sheet-foot">
          <button type="button" className="shipment-m-sheet-save" disabled={!changed || saving} onClick={() => onSave(selected)}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const VehicleSheet = ({ shipment, saving, onClose, onSave }) => {
  const sheetRef = useBottomSheet(true, onClose);
  const [selected, setSelected] = useState(shipment.vehicleType || 'fourWheeler');
  const options = [
    { value: 'fourWheeler', icon: '🚚', label: 'Four-wheeler' },
    { value: 'twoWheeler', icon: '🛵', label: 'Two-wheeler' },
  ];
  const changed = selected !== shipment.vehicleType;

  return (
    <div className="shipment-m-sheet-overlay" role="presentation" onMouseDown={onClose}>
      <div className="shipment-m-sheet" role="dialog" aria-modal="true" aria-labelledby="vehicle-sheet-title" ref={sheetRef} onMouseDown={(event) => event.stopPropagation()}>
        <div className="shipment-m-sheet-handle" aria-hidden="true" />
        <div className="shipment-m-sheet-head">
          <h2 id="vehicle-sheet-title">Vehicle type</h2>
          <button type="button" aria-label="Close" onClick={onClose} disabled={saving}>×</button>
        </div>
        <p className="shipment-m-sheet-note">You can change this until the ride starts.</p>
        {shipment.vehicleTypeAdvisory && <p className="shipment-m-sheet-advisory">{shipment.vehicleTypeAdvisory}</p>}
        <div className="shipment-m-sheet-list">
          {options.map((option) => (
            <button type="button" key={option.value} className={`shipment-m-opt-row ${selected === option.value ? 'is-selected' : ''}`} onClick={() => setSelected(option.value)}>
              <span className="shipment-m-opt-avatar" aria-hidden="true">{option.icon}</span>
              <span className="shipment-m-opt-copy">
                <strong>{option.label}</strong>
                {option.value === shipment.vehicleType && Number.isFinite(Number(shipment.capacityPoints)) && <span>{shipment.capacityPoints} capacity points</span>}
              </span>
              {selected === option.value && <span className="shipment-m-opt-check" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
        <div className="shipment-m-sheet-foot">
          <button type="button" className="shipment-m-sheet-save" disabled={!changed || saving} onClick={() => onSave(selected)}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const deliveredTime = (value) => value ? new Date(value).toLocaleTimeString('en-IN', {
  timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit',
}) : '';

// One card per order. Before/during the ride it shows the order details and
// the Call / Navigate / Invoice actions. During the ride a pending order can be
// delivered inline (OTP required; photo + note optional).
const MobileOrderCard = ({ order, stop, isNext, focused, cardRef, onFocusStop, canOpenOrders, editable, removable, disabled, onMove, onRemove, rideStarted, onDeliverOrder }) => {
  const focusStop = () => { if (onFocusStop && stop?.sequence != null) onFocusStop(stop.sequence); };
  const [expanded, setExpanded] = useState(false);
  const [otp, setOtp] = useState('');
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const products = [
    { code: 'panels', short: 'P', value: Number(order.panels || 0) },
    { code: 'inverters', short: 'I', value: Number(order.inverters || 0) },
    { code: 'batteries', short: 'B', value: Number(order.batteries || 0) },
    { code: 'bos', short: 'BOS', value: Number(order.bos || 0) },
  ];
  const address = order.address || [stop?.area, order.pincode].filter(Boolean).join(', ') || 'Address unavailable';
  const statusLabel = order.delivered ? 'Delivered' : (isNext ? 'Up next' : order.status);
  const statusClass = order.delivered ? 'is-delivered' : (isNext ? 'is-next' : '');

  const confirmDelivery = async (event) => {
    event.stopPropagation();
    if (saving) return;
    setSaving(true);
    const ok = await onDeliverOrder(order.id, { otp, note, odometerFile: photo });
    setSaving(false);
    if (ok) { setExpanded(false); setOtp(''); setNote(''); setPhoto(null); }
  };

  return (
    <article
      ref={cardRef}
      className={`shipment-m-order-card ${order.delivered ? 'is-delivered' : ''} ${isNext ? 'is-next' : ''} ${focused ? 'is-focused' : ''}`}
      onMouseEnter={onFocusStop ? focusStop : undefined}
      onClick={onFocusStop ? focusStop : undefined}
    >
      <div className="shipment-m-order-top">
        <span className="shipment-m-order-stop">{order.delivered ? '✓' : stop?.sequence || '—'}</span>
        <div className="shipment-m-order-id-copy">
          {canOpenOrders
            ? <Link to={`/orders/${order.id}`} className="shipment-m-order-id">{order.id}</Link>
            : <strong className="shipment-m-order-id">{order.id}</strong>}
          <span className="shipment-m-order-customer" title={order.customer}>{order.customer || 'Customer'}</span>
        </div>
        <span className={`shipment-m-order-badge ${statusClass}`}>{statusLabel}</span>
      </div>

      {order.delivered ? (
        <div className="shipment-m-order-delivered">
          <span className="shipment-m-order-delivered-time">Delivered{order.deliveredAt ? ` · ${deliveredTime(order.deliveredAt)}` : ''}</span>
        </div>
      ) : (
        <>
          {/* Action row directly under the header: Call / Navigate / Invoice. */}
          <div className="shipment-m-order-cta" onClick={(e) => e.stopPropagation()}>
            {order.phone
              ? <a href={`tel:${order.phone}`} className="shipment-m-order-call"><PhoneIcon /> Call</a>
              : <span className="shipment-m-order-call is-disabled"><PhoneIcon /> Call</span>}
            <a href={navigationUrl(order, stop)} target="_blank" rel="noopener noreferrer" className={`shipment-m-order-navigate ${hasConfirmedPin(stop) ? 'is-pinned' : ''}`}><NavigateIcon /> Navigate</a>
            <OrderDocsActions orderId={order.id} variant="action" />
          </div>

          <p className="shipment-m-order-address" title={address}>{address}</p>
          <div className="shipment-m-order-products" aria-label={`Products in ${order.id}`}>
            {products.map((product) => <span className={`is-${product.code}`} key={product.code}><b>{product.short}</b><strong>{product.value}</strong></span>)}
          </div>
          <div className="shipment-m-order-meta">
            <span>{order.pincode || 'No pincode'}</span>
            <span>{formatDate(order.expectedDeliveryDate)}</span>
            <span>{order.loadPoints} pts</span>
          </div>

          {rideStarted && !expanded && (
            <button type="button" className="shipment-m-order-deliver" disabled={disabled} onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>Mark delivered</button>
          )}
          {rideStarted && expanded && (
            <div className="shipment-m-deliver-panel" onClick={(e) => e.stopPropagation()}>
              <label className="shipment-m-deliver-field">
                <span>Customer OTP <b>*</b></span>
                <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp} placeholder="Enter OTP" disabled={saving} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} autoFocus />
              </label>
              <OdometerCapture id={`deliver-photo-${order.id}`} label="Add photo (optional)" file={photo} onChange={setPhoto} disabled={saving} />
              <label className="shipment-m-deliver-field">
                <span>Delivery note (optional)</span>
                <input type="text" maxLength={500} value={note} placeholder="e.g. left with security" disabled={saving} onChange={(e) => setNote(e.target.value)} />
              </label>
              <div className="shipment-m-deliver-actions">
                <button type="button" className="shipment-m-deliver-cancel" disabled={saving} onClick={() => { setExpanded(false); }}>Cancel</button>
                <button type="button" className="shipment-m-deliver-confirm" disabled={saving || !otp.trim()} onClick={confirmDelivery}>{saving ? 'Confirming…' : 'Confirm delivery'}</button>
              </div>
            </div>
          )}

          {editable && (
            <div className="shipment-m-order-actions">
              <button type="button" className="shipment-order-move" disabled={disabled} onClick={(e) => { e.stopPropagation(); onMove(order); }}>Move order</button>
              <button type="button" className="shipment-order-remove" disabled={disabled || !removable} title={removable ? `Remove ${order.id} from this shipment` : 'Discard the shipment to release its last order'} onClick={(e) => { e.stopPropagation(); onRemove(order); }}>Remove</button>
            </div>
          )}
        </>
      )}
    </article>
  );
};

const MobileOrderList = ({ orders, orderCount, routeSummary, nextStopSequence, rideStarted, stopByOrderId, canOpenOrders, canEditOrders, canAddOrders, canDiscard, editOrders, actionLoading, onToggleEdit, onAddOrders, onDiscard, onMove, onRemove, onDeliverOrder, cardRefs, focusedStop, onFocusStop }) => (
  <section className="shipment-m-orders">
    <div className="shipment-m-section-head">
      <h2>Orders <span>{orderCount}</span></h2>
      <div className="shipment-m-order-tools">
        {canAddOrders && <button type="button" className="shipment-m-add-orders" disabled={actionLoading} onClick={onAddOrders}>Add orders</button>}
        {canEditOrders && (
          <button type="button" className={`shipment-m-edit-toggle ${editOrders ? 'is-active' : ''}`} disabled={actionLoading} onClick={onToggleEdit}>
            {editOrders ? 'Done' : 'Edit orders'}
          </button>
        )}
        {canDiscard && <button type="button" className="shipment-m-discard" disabled={actionLoading} onClick={onDiscard}>Discard</button>}
      </div>
    </div>
    <div className="shipment-m-route-summary"><RouteIcon /><span>{routeSummary}</span></div>
    {editOrders && canEditOrders && <p className="shipment-m-edit-note">Move or remove complete orders only. Released orders return to planning, and affected routes rebuild automatically.</p>}
    <div className="shipment-m-order-cards">
      {orders.map((order) => {
        const stop = stopByOrderId.get(String(order.id));
        const isNext = nextStopSequence != null && !order.delivered && stop?.sequence === nextStopSequence;
        return (
          <MobileOrderCard
            key={order.id}
            order={order}
            stop={stop}
            isNext={isNext}
            rideStarted={rideStarted}
            onDeliverOrder={onDeliverOrder}
            focused={focusedStop != null && stop?.sequence === focusedStop}
            cardRef={cardRefs && stop?.sequence != null ? (node) => { if (node) cardRefs.current[String(stop.sequence)] = node; } : undefined}
            onFocusStop={onFocusStop}
            canOpenOrders={canOpenOrders}
            editable={editOrders && canEditOrders}
            removable={orderCount > 1}
            disabled={actionLoading}
            onMove={onMove}
            onRemove={onRemove}
          />
        );
      })}
    </div>
  </section>
);

// (The old MobileRouteCard with its repeated per-stop list was removed; the
// map now leads the page and the order cards carry each stop number.)

// Desktop: sticky right-column map (520px) with overlay route label, an expand
// button, and the legend collapsed behind an info toggle.
const DesktopMapPanel = ({ shipment, currentLocation, pendingPin, focusedStop, onStopSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const routeReady = shipment.routeStatus === 'ready';
  return (
    <aside className="shipment-d-map">
      <div className="shipment-d-map-canvas">
        <GoogleShipmentMap shipment={shipment} currentLocation={currentLocation} pendingPin={pendingPin} interactive={false} focusedStop={focusedStop} onStopSelect={onStopSelect} />
        <span className={`shipment-m-map-route ${routeReady ? '' : 'is-fallback'}`}>{routeReady ? 'Google optimized route' : 'Pincode route fallback'}</span>
        <div className="shipment-d-map-tools">
          <button type="button" className="shipment-m-map-expand" aria-label="Map legend" aria-expanded={legendOpen} onClick={() => setLegendOpen((v) => !v)}><InfoIcon /></button>
          <button type="button" className="shipment-m-map-expand" aria-label="Expand map" onClick={() => setExpanded(true)}><ExpandIcon /></button>
        </div>
        {legendOpen && (
          <div className="shipment-d-map-legend" role="note">
            <span><i className="is-hub">H</i>Hub</span>
            <span><i className="is-confirmed">✓</i>Confirmed pin</span>
            <span><i className="is-address" />Full address</span>
            <span><i className="is-pincode" />Pincode area</span>
          </div>
        )}
      </div>
      {expanded && (
        <div className="shipment-m-map-overlay" role="dialog" aria-modal="true" aria-label="Delivery route map">
          <button type="button" className="shipment-m-map-close" aria-label="Close map" onClick={() => setExpanded(false)}>×</button>
          <div className="shipment-m-map-overlay-canvas">
            <GoogleShipmentMap shipment={shipment} currentLocation={currentLocation} pendingPin={pendingPin} interactive={false} focusedStop={focusedStop} onStopSelect={onStopSelect} />
          </div>
        </div>
      )}
    </aside>
  );
};

// Map-first card: full-width 240px map directly under the header, with an
// overlay route-status label (bottom-left) and an expand-to-fullscreen button.
const MobileMapCard = ({ shipment, currentLocation, pendingPin, focusedStop, onStopSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const routeReady = shipment.routeStatus === 'ready';
  return (
    <section className="shipment-m-map">
      <div className="shipment-m-map-canvas">
        <GoogleShipmentMap shipment={shipment} currentLocation={currentLocation} pendingPin={pendingPin} interactive={false} focusedStop={focusedStop} onStopSelect={onStopSelect} />
        <span className={`shipment-m-map-route ${routeReady ? '' : 'is-fallback'}`}>{routeReady ? 'Google optimized route' : 'Pincode route fallback'}</span>
        <button type="button" className="shipment-m-map-expand" aria-label="Expand map" onClick={() => setExpanded(true)}><ExpandIcon /></button>
      </div>
      {expanded && (
        <div className="shipment-m-map-overlay" role="dialog" aria-modal="true" aria-label="Delivery route map">
          <button type="button" className="shipment-m-map-close" aria-label="Close map" onClick={() => setExpanded(false)}>×</button>
          <div className="shipment-m-map-overlay-canvas">
            <GoogleShipmentMap shipment={shipment} currentLocation={currentLocation} pendingPin={pendingPin} interactive={false} focusedStop={focusedStop} onStopSelect={onStopSelect} />
          </div>
        </div>
      )}
    </section>
  );
};

const MobileLoadCard = ({ shipment, vehicleLabel, utilization, remainingCapacity, pointsAboveTarget }) => (
  <section className="card shipment-m-load-card">
    <div className="shipment-m-load-top">
      <h2>{vehicleLabel} load</h2>
      <strong className={pointsAboveTarget ? 'is-over' : ''}>{utilization}%</strong>
    </div>
    <div className={`shipment-m-loadbar-track ${pointsAboveTarget ? 'is-over' : ''}`}>
      <span style={{ width: `${Math.min(utilization, 100)}%` }} />
    </div>
    <div className="shipment-m-load-row">
      <span>Used <strong>{shipment.loadPoints}</strong></span>
      <span>Target <strong>{shipment.capacityPoints}</strong></span>
      <span>{pointsAboveTarget ? 'Over' : 'Available'} <strong>{pointsAboveTarget || remainingCapacity}</strong></span>
    </div>
  </section>
);

const MobileMoveSheet = ({ orderToMove, moveTargets, selectedTarget, moveTargetsLoading, moveError, actionLoading, onSelect, onClose, onConfirm }) => {
  const sheetRef = useBottomSheet(true, onClose);
  return (
    <div className="shipment-m-sheet-overlay" role="presentation" onMouseDown={onClose}>
      <div className="shipment-m-sheet" role="dialog" aria-modal="true" aria-labelledby="move-sheet-title" ref={sheetRef} onMouseDown={(event) => event.stopPropagation()}>
        <div className="shipment-m-sheet-handle" aria-hidden="true" />
        <div className="shipment-m-sheet-head">
          <h2 id="move-sheet-title">Move order</h2>
          <button type="button" aria-label="Close" onClick={onClose} disabled={actionLoading}>×</button>
        </div>
        <p className="shipment-m-sheet-note">{orderToMove.id} · all products move together. Both routes rebuild.</p>
        <div className="shipment-m-sheet-list">
          {moveTargetsLoading ? <p className="shipment-m-sheet-empty">Finding compatible shipments…</p>
            : moveError ? <div className="shipment-api-error">{moveError}</div>
              : moveTargets.length ? moveTargets.map((target) => {
                const projectedLoad = Number(target.projectedLoadPoints ?? target.loadPoints) || 0;
                const percent = target.capacityPoints ? Math.round((projectedLoad / target.capacityPoints) * 100) : 0;
                return (
                  <button type="button" key={target.id}
                    className={`shipment-m-opt-row ${selectedTarget === target.id ? 'is-selected' : ''} ${!target.available ? 'is-disabled' : ''}`}
                    disabled={!target.available || actionLoading} onClick={() => onSelect(target.id)}>
                    <span className="shipment-m-opt-copy">
                      <strong>{target.id}</strong>
                      <span>{target.area} · {(target.pincodes || []).join(', ')}</span>
                      <span>{VEHICLE_LABELS[target.vehicleType] || 'Four-wheeler'} · {target.orderCount} orders</span>
                    </span>
                    <span className="shipment-m-move-load">
                      <strong>{percent}%</strong>
                      {target.available ? <span>{projectedLoad}/{target.capacityPoints} pts</span> : <span>{target.unavailableReason}</span>}
                    </span>
                  </button>
                );
              }) : <p className="shipment-m-sheet-empty">No compatible pending shipment for this order.</p>}
        </div>
        <div className="shipment-m-sheet-foot">
          <button type="button" className="shipment-m-sheet-save" disabled={!selectedTarget || actionLoading || moveTargetsLoading} onClick={onConfirm}>
            {actionLoading ? 'Moving…' : 'Move order'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Per-order document actions: view/download the order's invoice and e-way bill.
// The shipment payload carries no invoice reference, so the GeneratedInvoice
// record is fetched lazily by order id on first expand (mirrors OrderDetail).
const OrderDocsActions = ({ orderId, variant }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [orderFiles, setOrderFiles] = useState([]);
  const [busy, setBusy] = useState('');

  const loadInvoice = async () => {
    setLoading(true);
    try {
      const res = await getGeneratedInvoices({ order_id: orderId });
      const invoices = res?.data || [];
      const active = invoices.find((inv) => inv.generation_method === 'gstzen' && inv.cancellation_status !== 'approved')
        || invoices.find((inv) => inv.cancellation_status !== 'approved')
        || invoices[0]
        || null;
      const filesRes = await getOrderFiles(orderId);
      setInvoice(active);
      setOrderFiles(filesRes?.data || []);
      setLoaded(true);
    } catch (error) {
      toast.error(error.message || 'Could not load documents for this order');
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) loadInvoice();
  };

  const runAction = async (key, fn) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
    } catch (error) {
      toast.error(error.message || 'Document is not available');
    } finally {
      setBusy('');
    }
  };

  const viewInvoice = () => runAction('view-invoice', async () => {
    const res = await getInvoiceDownloadUrl(invoice.id);
    openUrl(res.data.url);
  });
  const downloadInvoice = () => runAction('dl-invoice', async () => {
    const file = orderFiles.find((item) => item.file_type === 'Invoice');
    if (!file) throw new Error('Invoice file is not available in Order Documents');
    await downloadOrderFile(orderId, file.id);
  });
  const viewEway = () => runAction('view-eway', async () => {
    const res = await getEwayBillDownloadUrl(invoice.id);
    openUrl(res.data.url);
  });
  const downloadEway = () => runAction('dl-eway', async () => {
    const file = orderFiles.find((item) => item.file_type === 'E-Way Bill');
    if (!file) throw new Error('E-way bill is not available in Order Documents');
    await downloadOrderFile(orderId, file.id);
  });

  const hasInvoiceFile = orderFiles.some((item) => item.file_type === 'Invoice');
  const hasEway = orderFiles.some((item) => item.file_type === 'E-Way Bill');

  return (
    <div className={`shipment-docs ${open ? 'is-open' : ''} ${variant === 'action' ? 'is-action' : ''}`}>
      <button type="button" className="shipment-docs-toggle" aria-expanded={open} onClick={toggle}>
        {variant === 'action' ? <><DocIcon /><span>Invoice</span></> : <span>Invoice &amp; e-way bill</span>}
        <svg className="shipment-docs-caret" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <div className="shipment-docs-body">
          {loading ? <p className="shipment-docs-status">Loading documents…</p>
            : !invoice ? <p className="shipment-docs-status">No invoice generated for this order yet.</p>
              : (
                <>
                  <div className="shipment-docs-row">
                    <span className="shipment-docs-label">Invoice{invoice.invoice_number ? ` · ${invoice.invoice_number}` : ''}</span>
                    <div className="shipment-docs-actions">
                      <button type="button" disabled={!hasInvoiceFile || !!busy} onClick={viewInvoice}>{busy === 'view-invoice' ? '…' : 'View'}</button>
                      <button type="button" disabled={!hasInvoiceFile || !!busy} onClick={downloadInvoice}>{busy === 'dl-invoice' ? '…' : 'Download'}</button>
                    </div>
                  </div>
                  {!hasInvoiceFile && <p className="shipment-docs-note">Invoice PDF not attached yet.</p>}
                  <div className="shipment-docs-row">
                    <span className="shipment-docs-label">E-way bill</span>
                    <div className="shipment-docs-actions">
                      <button type="button" disabled={!hasEway || !!busy} onClick={viewEway}>{busy === 'view-eway' ? '…' : 'View'}</button>
                      <button type="button" disabled={!hasEway || !!busy} onClick={downloadEway}>{busy === 'dl-eway' ? '…' : 'Download'}</button>
                    </div>
                  </div>
                  {!hasEway && <p className="shipment-docs-note">No e-way bill generated for this order.</p>}
                </>
              )}
        </div>
      )}
    </div>
  );
};

export default ShipmentDetail;
